import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang, localName } from "../i18n";
import Seo from "../components/Seo";
import ChefCard from "../components/ChefCard";
import { Eyebrow, Reveal } from "../components/motion";

export default function Chefs() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const { data: cuisines } = useQuery({ queryKey: ["cuisines"], queryFn: () => api.get("/cuisines").then((r) => r.data) });

  const filterKeys = ["cuisine_id", "city", "max_price", "min_rating", "language", "guests", "date"];
  const query = useMemo(() => {
    const q = new URLSearchParams();
    filterKeys.forEach((k) => params.get(k) && q.set(k, params.get(k)));
    return q.toString();
  }, [params]); // eslint-disable-line

  const { data: chefs, isLoading, isError, refetch } = useQuery({
    queryKey: ["chefs", query],
    queryFn: () => api.get(`/chefs${query ? `?${query}` : ""}`).then((r) => r.data),
  });

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p, { replace: true });
  };

  const field = "lux-input";

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("chefs.title")} — Chef à Domicile`} description={t("chefs.sub")} />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-16 md:py-24">
        <Reveal>
          <Eyebrow>{t("nav.chefs")}</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-4">{t("chefs.title")}</h1>
          <p className="text-neutral-400 max-w-2xl mb-12">{t("chefs.sub")}</p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 bg-coal border border-white/8 p-5" data-testid="chefs-filters">
          <div>
            <label className="lux-label">{t("chefs.cuisine")}</label>
            <select data-testid="filter-cuisine" className={field} value={params.get("cuisine_id") || ""} onChange={(e) => set("cuisine_id", e.target.value)}>
              <option value="" className="bg-coal">{t("common.all")}</option>
              {cuisines?.map((c) => <option key={c.id} value={c.id} className="bg-coal">{localName(c, lang)}</option>)}
            </select>
          </div>
          <div>
            <label className="lux-label">{t("chefs.city")}</label>
            <input data-testid="filter-city" className={field} placeholder="Paris" value={params.get("city") || ""} onChange={(e) => set("city", e.target.value)} />
          </div>
          <div>
            <label className="lux-label">{t("chefs.maxPrice")}</label>
            <input data-testid="filter-max-price" type="number" className={field} placeholder="€" value={params.get("max_price") || ""} onChange={(e) => set("max_price", e.target.value)} />
          </div>
          <div>
            <label className="lux-label">{t("chefs.minRating")}</label>
            <select data-testid="filter-min-rating" className={field} value={params.get("min_rating") || ""} onChange={(e) => set("min_rating", e.target.value)}>
              <option value="" className="bg-coal">—</option>
              {[3, 4, 4.5].map((r) => <option key={r} value={r} className="bg-coal">{r}+ ★</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button data-testid="filter-clear-btn" onClick={() => setParams({}, { replace: true })}
              className="text-xs uppercase tracking-widest text-neutral-400 hover:text-gold transition-colors py-3">
              {t("chefs.clear")}
            </button>
          </div>
        </div>

        {isLoading && <p className="text-neutral-500 py-20 text-center" data-testid="chefs-loading">{t("common.loading")}</p>}
        {isError && (
          <div className="py-20 text-center">
            <p className="text-neutral-400 mb-4">{t("common.error")}</p>
            <button onClick={() => refetch()} className="text-gold gold-underline text-sm" data-testid="chefs-retry-btn">{t("common.retry")}</button>
          </div>
        )}
        {chefs && (
          <>
            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-8" data-testid="chefs-count">{t("chefs.count", { n: chefs.length })}</p>
            {chefs.length === 0 ? (
              <p className="text-neutral-400 py-16 text-center border border-white/8" data-testid="chefs-empty">{t("chefs.noResults")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="chefs-grid">
                {chefs.map((c, i) => <ChefCard key={c.id} chef={c} index={i} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
